import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import ThreeDot from "../../common/ThreeDot";
import { useSelector } from "react-redux";
import { getAllWallets } from "../../../services/wallet.service";
import EditButton from "../../common/EditButton";
import ViewDetailsButton from "../../common/ViewDetailsButton";
import { getAllWalletsGraphQL } from "../../../services/graphql.service";
import { debounce } from "lodash";

export default function Wallet() {
  const [wallets, setWallets] = useState([]);
  const { token } = useSelector((state) => state?.auth);
  const [next, setNext] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalEntries, setTotalEntries] = useState(null);
  const itemsPerPage = 10;
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const fetchWallets = async (page = 1) => {
    try {
      const params = { page };
      const res = await getAllWallets(token, params);
      // console.log(res);

      if (res && res.status >= 200 && res.status < 300) {
        // console.log(res.data);

        setWallets(res?.data?.results?.user_wallets);
        setNext(res?.data?.next);
        setPrevious(res?.data?.previous);
        setCurrentPage(page);
        setTotalEntries(res?.data?.count || null);
        setTotalPages(Math.ceil((res?.data?.count || 0) / itemsPerPage));
      } else if (res?.status == 404) {
        // console.log(res?.data?.error);
      } else {
        Swal.fire(
          "Error",
          res?.data?.message ||
            res?.data?.error ||
            "Failed to fetch wallet data. Please try again.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error fetching wallets:", error);
      Swal.fire(
        "Error",
        error?.response?.data?.error ||
          "A network or server error occurred. Please try again later.",
        "error"
      );
    }
  };

  const fetchWalletsGraphQL = async (page = 1, search) => {
    console.log("Fetching Wallets:", { search, page });

    try {
      const res = await getAllWalletsGraphQL(token, page, search);

      if (res && res.status >= 200 && res.status < 300) {
        console.log("Wallets fetched successfully:", res.data);

        setWallets(res?.data?.data?.custome_users?.results);
        setNext(res?.data?.data?.custome_users?.next);
        setPrevious(res?.data?.data?.custome_users?.previous);
        setCurrentPage(page);
        setTotalEntries(res?.data?.data?.custome_users?.count || null);
        setTotalPages(
          Math.ceil((res?.data?.data?.custome_users?.count || 0) / itemsPerPage)
        );
      } else {
        Swal.fire(
          "Error",
          res?.data?.message || "Failed to fetch Wallets. Please try again.",
          "error"
        );
      }
    } catch (error) {
      console.error("Error fetching Wallets:", error);
      Swal.fire(
        "Error",
        error?.response?.data?.error ||
          "A network or server error occurred. Please try again later.",
        "error"
      );
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    debouncedSearchHandler(value);
  };

  const debouncedSearchHandler = useCallback(
    debounce((value) => {
      setDebouncedSearch(value);
      setCurrentPage(1);
    }, 300),
    []
  );

  useEffect(() => {
    if (debouncedSearch) {
      fetchWalletsGraphQL(currentPage, debouncedSearch);
    } else {
      fetchWallets(currentPage);
    }
  }, [debouncedSearch, currentPage]);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const startEntry = (currentPage - 1) * itemsPerPage + 1;
  const endEntry = Math.min(currentPage * itemsPerPage, totalEntries);

  return (
    <div className="content-body">
      <div className="container-fluid">
        <div className="form-head d-flex mb-3 align-items-start justify-content-between">
          <div className="input-group search-area d-inline-flex">
            <input
              type="text"
              className="form-control"
              placeholder="Search here"
              onChange={(e) => handleSearchChange(e)}
            />
            <div className="input-group-append">
              <span className="input-group-text">
                <i className="fas fa-search"></i>
              </span>
            </div>
          </div>
          <div style={{ zIndex: 2 }}>
            <Link to="/retailer/wallet-add">
              <button className="btn btn-primary px-3 py-2 add-btn sticky-btn">
                Add Credit to Wallet
                <img
                  className="plus-sign ms-2"
                  src="/assets/images/kidskart/icon/add.png"
                  alt="Add"
                />
              </button>
            </Link>
          </div>
        </div>

        <div className="row">
          <div className="col-xl-12 col-xxl-12 col-lg-12 col-sm-12">
            <div className="card">
              <div className="card-body p-0">
                {wallets && wallets.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table order-request2">
                      <thead>
                        <tr>
                          <th>#</th>
                          <th>Retailer Name</th>
                          <th>Mail</th>
                          <th>Balance</th>
                          <th>Date</th>
                          {/* <th>Status</th> */}
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {wallets?.length > 0 &&
                          wallets.map((item, i) => (
                            <tr key={i}>
                              <td>
                                {(currentPage - 1) * itemsPerPage + i + 1}
                              </td>
                              <td>{item.retailer_name || item?.owner_name}</td>
                              <td>{item.user_email || item?.email}</td>
                              <td>
                                ₹
                                {(item.credit_amount * 1)?.toLocaleString(
                                  "en-IN",
                                  {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  }
                                )}
                              </td>
                              <td>
                                {" "}
                                {new Date(item?.created_at || item?.wallet_created_at)
                                  .toLocaleDateString("en-GB", {
                                    day: "2-digit",
                                    month: "long",
                                    year: "numeric",
                                  })
                                  .replace(" ", " ")
                                  .replace(/(\w+ \w+)/, "$1,")}
                              </td>
                              {/* <td>
                                <Link className="btn btn-success light btn-sm">
                                  ACTIVE
                                </Link>
                              </td> */}
                              <td>
                                <div className="d-flex align-items-center">
                                  <div className="dropdown ms-auto c-pointer">
                                    <ThreeDot />
                                    <div className="dropdown-menu dropdown-menu-end">
                                      <EditButton
                                        to={`/retailer/wallet-update`}
                                        id={item.retailer_id || item?.customer_id}
                                      />

                                      <ViewDetailsButton
                                        to={`/retailer/wallet-history`}
                                        id={item.retailer_id || item?.customer_id}
                                        name={"History"}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ))}
                        {wallets && wallets.length < 5 ? (
                          <tr style={{ height: "205px" }}></tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="my-5 py-5 text-center fs-4 fw-bold">
                    Please Add Retailer Wallet
                  </div>
                )}
              </div>
            </div>

            {/* Pagination Info */}
            {totalEntries && totalEntries > 10 && (
              <div className="dataTables_wrapper no-footer">
                <div className="dataTables_info">
                  Showing {startEntry} to {endEntry} of {totalEntries} entries
                </div>
                <div className="dataTables_paginate paging_simple_numbers">
                  <button
                    className={`btn btn-primary me-2 ${
                      !previous && "disabled"
                    }`}
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    Previous
                  </button>
                  <span>
                    {(() => {
                      const maxVisible = 7;
                      const pages = Array.from(
                        { length: totalPages },
                        (_, i) => i + 1
                      );
                      let start = Math.max(
                        currentPage - Math.floor(maxVisible / 2),
                        1
                      );
                      let end = start + maxVisible - 1;

                      if (end > totalPages) {
                        end = totalPages;
                        start = Math.max(end - maxVisible + 1, 1);
                      }

                      return pages.slice(start - 1, end).map((page) => (
                        <button
                          key={page}
                          className={`btn btn-sm mx-1 ${
                            currentPage === page
                              ? "btn-dark"
                              : "btn-outline-dark"
                          }`}
                          onClick={() => handlePageChange(page)}
                        >
                          {page}
                        </button>
                      ));
                    })()}
                  </span>
                  <button
                    className={`btn btn-primary ms-2 ${!next && "disabled"}`}
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
